import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Lock } from "lucide-react";
import { useLang } from "@/i18n";
import { LEGAL_LINKS, NAV } from "@/content/nav";
import { LogoFull } from "./Logo";

export const Footer = () => {
  const { lang, t } = useLang();
  const nav = NAV[lang];
  const legal = LEGAL_LINKS[lang];

  return (
    <footer data-testid="site-footer" className="border-t border-[#E4DDD0] bg-[#F3EFE6]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-7">
          <div className="lg:col-span-2">
            <LogoFull light={false} />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#5C6B64]">{t.footer.tagline}</p>
            <p className="mt-8 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-[#5C6B64]/80">
              {t.footer.legal}
            </p>
            <Link
              data-testid="footer-admin-link"
              to="/admin"
              className="mt-8 inline-flex items-center gap-2 border border-[#E4DDD0] bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] transition-colors duration-300 hover:border-[#C87D55] hover:text-[#B36C45]"
            >
              <Lock className="h-3.5 w-3.5" />
              {t.footer.admin}
            </Link>

            <h4 className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">
              Company Profile
            </h4>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a
                  data-testid="footer-profile-linkedin"
                  href="https://www.linkedin.com/company/columbus-management-consulting-gmbh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1.5 text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                >
                  LinkedIn
                  <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-profile-crunchbase"
                  href="https://www.crunchbase.com/organization/columbus-management-consulting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1.5 text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                >
                  Crunchbase
                  <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-profile-f6s"
                  href="https://www.f6s.com/columbus-management-consulting-gmbh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1.5 text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                >
                  F6S
                  <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </a>
              </li>
            </ul>

            <h4 className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">
              Company Information
            </h4>
            <dl className="mt-4 space-y-2.5" data-testid="footer-company-info">
              {[
                ["Legal Name", "Columbus Management Consulting GmbH"],
                ["Office", "Susenbergstrasse 104, 8044 Zürich"],
                ["Registry Number", "CH-020.4.044.478-1"],
                ["VAT Number", "CHE-486.721.940"],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#5C6B64]/70">{label}</dt>
                  <dd className="mt-0.5 text-[13px] text-[#0A1F18]">{value}</dd>
                </div>
              ))}
              <div>
                <dt className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#5C6B64]/70">Website</dt>
                <dd className="mt-0.5">
                  <a
                    data-testid="footer-info-website"
                    href="https://www.columbusmanagementconsulting.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                  >
                    www.columbusmanagementconsulting.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#5C6B64]/70">Email</dt>
                <dd className="mt-0.5">
                  <a
                    data-testid="footer-info-email"
                    href="mailto:contact@columbusmanagementconsulting.com"
                    className="text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                  >
                    contact@columbusmanagementconsulting.com
                  </a>
                </dd>
              </div>
            </dl>

            <a
              data-testid="footer-incorporation-download"
              href="/columbus-incorporation-dossier.pdf"
              download="Columbus-Management-Consulting-GmbH-Incorporation.pdf"
              className="mt-8 inline-flex items-center gap-2 border border-[#E4DDD0] bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] transition-colors duration-300 hover:border-[#C87D55] hover:text-[#B36C45]"
            >
              <Download className="h-3.5 w-3.5" />
              Incorporation Document
            </a>
          </div>

          {nav.groups.slice(0, 4).map((group) => (
            <div key={group.key}>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">{group.label}</h4>
              <ul className="mt-6 space-y-2.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      data-testid={`footer-${item.to.slice(1).replaceAll("/", "-")}`}
                      to={item.to}
                      className="group flex items-center gap-1.5 text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                    >
                      {item.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">
              {nav.groups[4].label}
            </h4>
            <ul className="mt-6 space-y-2.5">
              {nav.groups[4].items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              {nav.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-[13px] text-[#5C6B64] transition-colors duration-300 hover:text-[#0A1F18]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/contact" className="text-[13px] text-[#5C6B64] transition-colors hover:text-[#0A1F18]">
                  Contact
                </Link>
              </li>
            </ul>
            <h4 className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">
              {t.footer.legalTitle}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {legal.map((item) => (
                <li key={item.to}>
                  <Link
                    data-testid={`footer-legal-${item.to.slice(1)}`}
                    to={item.to}
                    className="text-[13px] text-[#5C6B64]/80 transition-colors duration-300 hover:text-[#0A1F18]"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-[#E4DDD0] pt-8 sm:flex-row">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B64]/80">
            {t.footer.rights}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B64]/80">
            Susenbergstrasse 104, 8044 Zürich
          </span>
        </div>
      </div>
    </footer>
  );
};
